import { Component, Prop, h, Host, Element } from '@stencil/core';

const COMPONENT_NAME = 'sn-cta-type-8';

interface HiddenField {
  name?: string;
  type?: string;
  value?: string;
}

interface FieldMessage {
  field?: string;
  requiredMessage?: string;
  invalidMessage?: string;
}

/**
 * Full-width call-to-action banner promoting free-trial sign-up.
 * Centered single-column layout with heading, description, and stacked actions inside an `<me-container>`.
 * Differs from sibling CTAs by exposing a `variant` prop for light/dark theming and embedding a free-trial form as the primary action.
 *
 * @version 2.0.0
 * @category SignNow
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]>
 * @slot description - <me-text tag="p">
 * @slot action-primary - {required}, <form-free-trial-type-1>, <me-button>. When `demo-form` is set, the slotted button becomes the trigger that opens the embedded `sn-form-type-1` "Request a demo" modal.
 * @slot action-secondary - <me-button variant="ghost">
 * @example
 *   <sn-cta-type-8 variant="dark">
 *     <me-heading slot="heading" variant="2" tag="h2">Join over 28 million airSlate SignNow users</me-heading>
 *     <me-text slot="description" tag="p">Get the most out of your eSignature workflows. Start your free trial today — no credit card required.</me-text>
 *     <form-free-trial-type-1 slot="action-primary" button-variant="primary" mobile-layout="stacked" note="No credit card required" submit-text="Start your free trial"></form-free-trial-type-1>
 *     <me-button slot="action-secondary" variant="ghost" href="/pricing" tag="a">See pricing plans</me-button>
 *   </sn-cta-type-8>
 * @example
 *   <!-- "Request a demo" opens the sn-form-type-1 modal and redirects on success (same pattern as type-10/type-11) -->
 *   <sn-cta-type-8 variant="dark" demo-form form-redirect-url="https://www.signnow.com/thankyou" form-show-message="false">
 *     <me-heading slot="heading" variant="2" tag="h2">Join over 28 million airSlate SignNow users</me-heading>
 *     <me-button slot="action-primary" variant="primary" tag="button">Request a demo</me-button>
 *   </sn-cta-type-8>
 */
@Component({
  tag: 'sn-cta-type-8',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCtaType8 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Visual theme variant. `dark` renders on the navy background (default for in-page CTAs);
   * `light` renders on a white background.
   * @type {string}
   * @default dark
   * {enum}: ['light', 'dark']
   */
  @Prop({ reflect: true }) variant: 'light' | 'dark' = 'dark';

  /**
   * When enabled, the `action-primary` slot becomes the trigger for an embedded
   * `sn-form-type-1` "Request a demo" modal (same behavior as `sn-cta-type-10`/`-11`).
   * When off (default), the primary slot renders inline as-is (link, free-trial form, etc.).
   * @default false
   */
  @Prop({ attribute: 'demo-form' }) demoForm: boolean = false;

  /**
   * Form `action` URL the embedded "Request a demo" form submits to.
   * @default '#'
   */
  @Prop({ attribute: 'form-action' }) formAction: string = '#';

  /**
   * URL the user is redirected to after the embedded "Request a demo" form submits successfully.
   * When omitted, no redirect occurs.
   */
  @Prop({ attribute: 'form-redirect-url' }) formRedirectUrl?: string;

  /**
   * Show the optional "Questions or comments" field inside the embedded "Request a demo" form.
   * @default true
   */
  @Prop({ attribute: 'form-show-message' }) formShowMessage: boolean = true;

  /**
   * Show the "Job title" field inside the embedded "Request a demo" form.
   * @default true
   */
  @Prop({ attribute: 'form-show-job-title' }) formShowJobTitle: boolean = true;

  /**
   * Show the "Company size" field inside the embedded "Request a demo" form.
   * @default true
   */
  @Prop({ attribute: 'form-show-company-size' }) formShowCompanySize: boolean = true;

  /**
   * Extra hidden form fields posted with the embedded "Request a demo" form
   * (e.g. `industry_name`, `_token`, `gclid`). Passed straight through to the
   * embedded `sn-form-type-1` as its `hiddenFields`. Accepts a real array or a
   * JSON string of `{ name, type?, value? }` objects.
   * @type {array}
   * @formHiddenFields.type {object}
   * @formHiddenFields.property {string} name - Field name posted with the form (e.g. "gclid", "industry_name")
   * @formHiddenFields.property {string} type - Native input type for the rendered field; defaults to "hidden"
   * @formHiddenFields.property {string} value - Value posted for the field; may be empty (backend/runtime fills dynamic ones)
   * @formHiddenFields.required name
   */
  @Prop({ attribute: 'form-hidden-fields' }) formHiddenFields?: HiddenField[] | string;

  /**
   * Per-field validation error text for the embedded "Request a demo" form. Passed straight
   * through to `sn-form-type-1` as its `fieldMessages`. Each entry overrides a field's default
   * `required` / `invalid` message; unset fields keep their defaults.
   * Valid field names: `first_name`, `last_name`, `email`, `phone`, `company_name`, `job_title`, `users`.
   * @type {array}
   * @formFieldMessages.type {object}
   * @formFieldMessages.property {string} field - Target field name (e.g. "email", "first_name")
   * @formFieldMessages.property {string} requiredMessage - Error text shown when the field is left empty
   * @formFieldMessages.property {string} invalidMessage - Error text shown when the value is invalid (email field)
   * @formFieldMessages.required field
   */
  @Prop({ attribute: 'form-field-messages' }) formFieldMessages?: FieldMessage[] | string;

  private renderPrimaryAction() {
    if (!this.demoForm) {
      return <slot name="action-primary"></slot>;
    }

    // The slotted "Request a demo" button becomes the modal's fallback trigger.
    // Carry its `variant` through so authors can style the trigger (e.g. bordered).
    const triggerVariant = (this.el.querySelector('[slot="action-primary"]')?.getAttribute('variant') ?? 'primary') as
      | 'primary'
      | 'secondary'
      | 'bordered'
      | 'tertiary'
      | 'detailed'
      | 'ghost';

    return (
      <sn-form-type-1
        class={`${COMPONENT_NAME}__demo-form`}
        action={this.formAction}
        redirectUrl={this.formRedirectUrl}
        triggerVariant={triggerVariant}
        showMessage={this.formShowMessage}
        showJobTitle={this.formShowJobTitle}
        showCompanySize={this.formShowCompanySize}
        hiddenFields={this.formHiddenFields}
        fieldMessages={this.formFieldMessages}
      >
        <slot name="action-primary" slot="trigger"></slot>
      </sn-form-type-1>
    );
  }

  render() {
    return (
      <Host>
        <section class={COMPONENT_NAME}>
          <me-container>
            <div class={`${COMPONENT_NAME}__inner`}>
              <div class={`${COMPONENT_NAME}__content`}>
                <slot name="heading"></slot>
                <slot name="description"></slot>
              </div>
              <div class={`${COMPONENT_NAME}__actions`}>
                {this.renderPrimaryAction()}
                <slot name="action-secondary"></slot>
              </div>
            </div>
          </me-container>
        </section>
      </Host>
    );
  }
}
